// frontend/app/PYQReview.js
import React, { useEffect, useMemo, useState } from "react";
import { Platform } from "react-native";
import { PYQ_API, getPaperDoc, savePaperDoc, getPdfUrl } from "./pyqApi";
// KaTeX
import "katex/dist/katex.min.css";
import katex from "katex";

// Web-only PDF viewer
let Document, Page, pdfjs;
if (typeof window !== "undefined" && Platform.OS === "web") {
  const pdf = require("react-pdf");
  Document = pdf.Document;
  Page = pdf.Page;
  pdfjs = require("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
}


const isObj = (v) => v && typeof v === "object";
const isInlineNode = (n) => isObj(n) && typeof n.kind === "string";
const isBlockNode  = (b) => isObj(b) && typeof b.block === "string";
const safeBlocks   = (arr) => (Array.isArray(arr) ? arr.filter(isBlockNode) : []);

function InlineRun({ node }) {
  if (!isInlineNode(node)) return null;
  switch (node.kind) {
    case "text": return <span>{node.text}</span>;
    case "formula_inline": return <KatexInline latex={node.latex} />;
    case "smiles": return <code style={{ fontSize: 12 }}>{node.smiles}</code>;
    case "image_inline":
      return <img src={node.data_uri} alt="" style={{ height: "1.1em", verticalAlign: "middle", margin: "0 2px" }} />;
    case "sup": return <sup>{(node.inlines||[]).map((n,i)=><InlineRun key={i} node={n} />)}</sup>;
    case "sub": return <sub>{(node.inlines||[]).map((n,i)=><InlineRun key={i} node={n} />)}</sub>;
    default: return null;
  }
}

function Paragraph({ block }) {
  const inlines = Array.isArray(block?.inlines) ? block.inlines.filter(isInlineNode) : [];
  if (!inlines.length) return null;
  return <p style={{ margin: "8px 0", lineHeight: 1.6 }}>{inlines.map((n,i)=><InlineRun key={i} node={n} />)}</p>;
}
function MaybeRangeBanner({ doc, qnoPrinted }) {
  const ranges = doc?.range_instructions || [];
  const rn = Number(qnoPrinted);
  const match = ranges.find(r => rn === r.from);
  if (!match) return null;
  return (
    <div style={{ background: "#fff7e6", border: "1px solid #ffd591", padding: 12, borderRadius: 8, margin: "12px 0" }}>
      <strong>Instructions for Questions {match.from}–{match.to}</strong>
      {(match.blocks || []).map((b, i) => <BlockRenderer key={i} block={b} />)}
    </div>
  );
}

function BlockRenderer({ block }) {
  if (!isBlockNode(block)) return null;
  if (block.block === "paragraph") return <Paragraph block={block} />;
  if (block.block === "formula_block") return <KatexBlock latex={block.latex} />;
//   if (block.block === "image")
//     return <div style={{ margin: "8px 0" }}><img src={block.data_uri} alt={block.alt||""} /></div>;
 if (block.block === "image")
  return (
    <div style={{ margin: "10px 0", textAlign: "center" }}>
      <img
        src={block.data_uri}
        alt={block.alt || ""}
        style={{ maxWidth: "100%", height: "auto", display: "inline-block" }}
      />
    </div>
  );

 
  if (block.block === "mmd") // raw table fallback
    return <pre style={{ background:"#fafafa", padding:8, overflowX:"auto" }}>{block.mmd}</pre>;
  return null;
}

function KatexInline({ latex }) {
  const html = useMemo(() => {
    try { return katex.renderToString(latex, { throwOnError: false }); }
    catch { return latex; }
  }, [latex]);
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}
function KatexBlock({ latex }) {
  const html = useMemo(() => {
    try { return katex.renderToString(latex, { throwOnError: false, displayMode: true }); }
    catch { return latex; }
  }, [latex]);
  return <div className="katex-block" dangerouslySetInnerHTML={{ __html: html }} />;
}


// Flatten a paragraph's inlines to plain text (for heuristics only)
function flattenInlines(inlines) {
  if (!Array.isArray(inlines)) return "";
  let out = "";
  for (const node of inlines) {
    if (!node || typeof node !== "object") continue;
    if (node.kind === "text") out += node.text || "";
    else if (node.kind === "sup" || node.kind === "sub") out += flattenInlines(node.inlines);
    // we ignore images for this heuristic
  }
  return out;
}

// Detect if a paragraph line *starts* like an option: (a) a) a. A) A.
function looksLikeOptionStart(txt) {
  const t = (txt || "").trim();
  if (!t) return false;
  if (/^(?:\(?[a-dA-D]\)|[a-dA-D][\.\)])\s+/.test(t)) return true; // (a) / a) / a. / A)
  if (/^OR\b/i.test(t)) return true; // lines that begin with "OR"
  return false;
}

// Remove option-like paragraphs from q.content so we don't double-render
function stemBlocksFromContent(q) {
  const blocks = safeBlocks(q?.content);
  const hasOptions = Array.isArray(q?.options) && q.options.length > 0;
  if (!hasOptions) return blocks;
  return blocks.filter((b) => {
    if (!b || b.block !== "paragraph") return true;
    const t = flattenInlines(b.inlines);
    return !looksLikeOptionStart(t);
  });
}
// function SubpartsViewer({ subparts }) {
//   if (!subparts || !subparts.length) return null;
//   return (
//     <div style={{ marginTop: 16 }}>
//       <h4 style={{ margin: "8px 0" }}>Parts</h4>
//       {subparts.map((part, idx) => (
//         <div
//           key={idx}
//           style={{
//             border: "1px solid #eee",
//             borderRadius: 8,
//             padding: 8,
//             marginBottom: 8,
//             background: "#fafafa",
//           }}
//         >
//           <div style={{ fontWeight: 600, marginBottom: 6 }}>{part.key}</div>
//           {(part.content || []).map((b, i) => <BlockRenderer key={i} block={b} />)}
//         </div>
//       ))}
//     </div>
//   );
// }
function SubpartsViewer({ subparts }) {
  if (!subparts || !subparts.length) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={{ margin: "8px 0" }}>Parts</h4>
      {subparts.map((part, idx) => (
        <div
          key={idx}
          style={{
            border: "1px solid #eee",
            borderRadius: 8,
            padding: 8,
            marginBottom: 8,
            background: "#fafafa",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{part.key}</div>

          {/* Part stem/content */}
          {safeBlocks(part.content).map((b, i) => (
            <BlockRenderer key={i} block={b} />
          ))}

          {/* Options inside this subpart (A/B/C/D) */}
          {Array.isArray(part.options) && part.options.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <h5 style={{ margin: "6px 0" }}>Options</h5>
              {part.options.map((opt, oidx) => (
                <div key={oidx} style={{ marginBottom: 6 }}>
                  <strong>
                    {opt.key_raw || `(${(opt.key || "").toUpperCase()})`}
                  </strong>{" "}
                  {safeBlocks(opt.content).map((b, i) => (
                    <BlockRenderer key={i} block={b} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// function OrGroupsViewer({ groups }) {
//   if (!groups || !groups.length) return null;
//   // groups is an array of groups; each group is an array of { key, content }
//   return (
//     <div style={{ marginTop: 16 }}>
//       <h4 style={{ margin: "8px 0" }}>Choose one (OR)</h4>
//       <div
//         style={{
//           display: "grid",
//           gridTemplateColumns: "1fr",
//           gap: 12,
//         }}
//       >
//         {groups.map((grp, gi) => (
//           <div
//             key={gi}
//             style={{
//               border: "1px solid #eee",
//               borderRadius: 8,
//               padding: 10,
//               background: "#f9fbff",
//             }}
//           >
//             {(grp || []).map((part, idx) => (
//               <div key={idx} style={{ marginBottom: 8 }}>
//                 <div style={{ fontWeight: 600, marginBottom: 6 }}>{part.key}</div>
//                 {(part.content || []).map((b, i) => <BlockRenderer key={i} block={b} />)}
//               </div>
//             ))}
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }
function OrGroupsViewer({ groups }) {
  if (!groups || !groups.length) return null;
  // groups: array of groups; each group = array of { key, content, options? }
  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={{ margin: "8px 0" }}>Choose one (OR)</h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 12,
        }}
      >
        {groups.map((grp, gi) => (
          <div
            key={gi}
            style={{
              border: "1px solid #eee",
              borderRadius: 8,
              padding: 10,
              background: "#f9fbff",
            }}
          >
            {(grp || []).map((part, idx) => (
              <div key={idx} style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{part.key}</div>

                {/* OR-branch subpart content */}
                {safeBlocks(part.content).map((b, i) => (
                  <BlockRenderer key={i} block={b} />
                ))}

                {/* And options inside that branch, if present */}
                {Array.isArray(part.options) && part.options.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {part.options.map((opt, oidx) => (
                      <div key={oidx} style={{ marginBottom: 6 }}>
                        <strong>
                          {opt.key_raw || `(${(opt.key || "").toUpperCase()})`}
                        </strong>{" "}
                        {safeBlocks(opt.content).map((b, i) => (
                          <BlockRenderer key={i} block={b} />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function OptionsViewer({ options }) {
  if (!options || !options.length) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <h4>Options</h4>
        {(options || []).map((opt, idx) => (
        <div key={idx} style={{ marginBottom: 8 }}>
            <strong>{opt.key_raw || `(${opt.key})`}</strong>{" "}
            {safeBlocks(opt.content).map((b, i) => <BlockRenderer key={i} block={b} />)}
        </div>
        ))}
    </div>
  );
}

export default function PYQReview({ paperId, lang = "en" }) {
  const [doc, setDoc] = useState(null);
  const [selIdx, setSelIdx] = useState(0);
  const [loadErr, setLoadErr] = useState(null);
  const q = doc?.questions?.[selIdx];

  useEffect(() => {
    async function load() {
      if (!paperId) return;
      try {
        const j = await getPaperDoc(paperId, lang);
        setDoc(j);
        setSelIdx(0);
        setLoadErr(null);
      } catch (err) {
        setDoc(null);
        setLoadErr(String(err?.message || err));
      }
    }
    load();
  }, [paperId, lang]);

  async function onSave(status = "verified") {
    if (!doc) return;
    const copy = JSON.parse(JSON.stringify(doc));
    if (!copy.questions[selIdx].review) copy.questions[selIdx].review = {};
    copy.questions[selIdx].review.status = status;
    await savePaperDoc(paperId, lang, copy);
    alert("Saved!");
  }

  if (!paperId) return <div style={{ padding: 24 }}>Missing paperId. Add <code>?paper=…</code> to URL.</div>;
  if (loadErr) return (
    <div style={{ padding: 24, color: "#b00" }}>
      <div><strong>Failed to load JSON.</strong></div>
      <div style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>{loadErr}</div>
      <div style={{ marginTop: 8 }}>API base: <code>{PYQ_API}</code></div>
    </div>
  );
  if (!doc) return <div style={{ padding: 24 }}>Loading {paperId} ({lang})…</div>;
  if (!q) return <div style={{ padding: 24 }}>No questions.</div>;

  return (
    <div className="pyq-root">
      {/* LEFT: PDF */}
      <div className="pyq-left">
        {Platform.OS !== "web" ? (
          <div style={{ padding: 12 }}>PDF preview available on Web only for MVP.</div>
        ) : (
          <Document file={getPdfUrl(paperId)}>
            {[...new Set(q.source.spans.map((s) => s.page))].map((p) => (
              <div key={p} style={{ position: "relative", marginBottom: 16 }}>
                <Page pageNumber={p} width={520} />
                {/* MVP overlays (approximate) */}
                {q.source.spans.filter((s) => s.page === p).map((s, i) => {
                  const [x1, y1, x2, y2] = s.bbox;
                  const style = {
                    position: "absolute",
                    top: 10 + i * 6,
                    left: 10 + i * 6,
                    width: 120,
                    height: 20,
                    border: "2px dashed #e33",
                    background: "rgba(255,0,0,0.05)",
                  };
                  return <div key={i} style={style} title={`p${p} bbox ${x1},${y1},${x2},${y2}`} />;
                })}
              </div>
            ))}
          </Document>
        )}
        <div className="pyq-nav">
          <button disabled={selIdx <= 0} onClick={() => setSelIdx((i) => i - 1)}>Prev</button>
          <span style={{ margin: "0 8px" }}>{selIdx + 1} / {doc.questions.length}</span>
          <button disabled={selIdx >= doc.questions.length - 1} onClick={() => setSelIdx((i) => i + 1)}>Next</button>
        </div>
      </div>

      {/* RIGHT: content viewer */}
        <div className="pyq-right">
        <h3>{paperId} — {lang.toUpperCase()}</h3>
        <div style={{ fontSize: 13, color: "#666" }}>
            <div>QID: {q.qid}</div>
            <div>Printed: {q.qno_printed || "-"}</div>
            <div>Type: {q.type}</div>
            <div>Marks: {q.marks || "-"}</div>
            <div>Status: {q.review?.status}</div>
        </div>

        {/* Section + instructions */}
        {Array.isArray(doc?.sections) && q.section && (
            (() => {
            const sec = doc.sections.find(s => s.sid === q.section);
            if (!sec) return null;
            return (
                <div style={{ marginTop: 10, padding: "8px 10px", background:"#f6fafc", border:"1px solid #e6eef4", borderRadius: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{sec.title || `Section ${sec.sid}`}</div>
                {safeBlocks(sec.instructions).map((b,i)=><BlockRenderer key={i} block={b} />)}
                </div>
            );
            })()
        )}

        <MaybeRangeBanner doc={doc} qnoPrinted={q.qno_printed} />

        {/* Question content */}
        <div style={{ marginTop: 12 }}>
            {safeBlocks(q.content).map((b, i) => <BlockRenderer key={i} block={b} />)}
        </div>

        {/* Options (MCQ / OR) */}
        {Array.isArray(q.options) && q.options.length > 0 && (
            <div style={{ marginTop: 12 }}>
            <h4>Options</h4>
            {q.options.map((opt, idx) => (
                <div key={idx} style={{ marginBottom: 8 }}>
                <strong>{opt.key_raw || `(${opt.key?.toUpperCase()})`}</strong>{" "}
                {safeBlocks(opt.content).map((b, i) => <BlockRenderer key={i} block={b} />)}
                </div>
            ))}
            </div>
        )}
        {/* NEW: non-MCQ structures */}
        {q.meta?.subparts ? <SubpartsViewer subparts={q.meta.subparts} /> : null}
        {q.meta?.or_groups ? <OrGroupsViewer groups={q.meta.or_groups} /> : null}

        <div className="pyq-actions">
            <button onClick={() => onSave("verified")}>Pass (Save)</button>
            <button onClick={() => onSave("needs_review")}>Needs Review</button>
            <button onClick={() => onSave("draft")}>Revert to Draft</button>
        </div>
        </div>



      <style>{`
        .pyq-root { display: grid; grid-template-columns: 560px 1fr; gap: 16px; padding: 16px; }
        .pyq-left { background: #fafafa; border: 1px solid #eee; padding: 8px; overflow: auto; height: calc(100vh - 48px); }
        .pyq-right { background: #fff; border: 1px solid #eee; padding: 16px; overflow: auto; height: calc(100vh - 48px); }
        .pyq-nav { display: flex; align-items: center; gap: 8px; padding: 8px 0; }
        .katex-block { margin: 8px 0; }
        .pyq-actions { margin-top: 16px; display: flex; gap: 8px; }
      `}</style>
    </div>
  );
}
